import {
  Box,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { FC } from 'react';
import { capitalize } from '../../../../Utils/StringUtils';
import { Todo, TodoFilter } from './TodoList';

interface TodosListProps {
  todos: Todo[];
  handleUpdateTask: (id: string) => void;
  toggleFilter: (value: TodoFilter) => void;
  clearCompleted: () => void;
  filter: TodoFilter;
}

const StyledText = styled(Typography)({
  fontSize: '18px',
  color: '#4c4c4dff',

  '&:hover': {
    fontWeight: 'bold',
  },

  '&:active': {
    transition: 'all 0.1s ease',
  },
});

const getEmptyLabel = (filter: TodoFilter) => {
  switch (filter) {
    case TodoFilter.ALL:
      return 'No todos';
    case TodoFilter.ACTIVE:
      return 'No active todos';
    case TodoFilter.COMPLETED:
      return 'No completed todos';
  }
};

const TodosList: React.FC<TodosListProps> = ({
  todos,
  handleUpdateTask,
  toggleFilter,
  clearCompleted,
  filter,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const isMobileStatusContent = (
    <Stack
      direction="column"
      spacing={1}
      className="w-full"
      justifyContent="space-between"
      alignItems="center"
      sx={{ paddingX: 4, paddingTop: 2 }}
    >
      <Box>
        <Stack direction="row" spacing={2} className="w-full">
          <StatusButton
            text="All"
            onClick={toggleFilter}
            value={TodoFilter.ALL}
            active={filter === TodoFilter.ALL}
          />
          <StatusButton
            text="Active"
            onClick={toggleFilter}
            value={TodoFilter.ACTIVE}
            active={filter === TodoFilter.ACTIVE}
          />
          <StatusButton
            text="Complete"
            onClick={toggleFilter}
            value={TodoFilter.COMPLETED}
            active={filter === TodoFilter.COMPLETED}
          />
        </Stack>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        className="w-full"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Box>
          <StyledText>{todos.length} items</StyledText>
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <ActionButton text="Clear Completed" onClick={clearCompleted} />
        </Box>
      </Stack>
    </Stack>
  );

  const isNotMobileStatusContent = (
    <Stack
      direction="row"
      spacing={2}
      className="w-full"
      justifyContent="space-between"
      alignItems="center"
      sx={{ paddingX: 4, paddingY: 2 }}
    >
      <Box>
        <StyledText>{todos.length} items</StyledText>
      </Box>

      <Box>
        <Stack direction="row" spacing={2} className="w-full">
          <StatusButton
            text="All"
            onClick={toggleFilter}
            value={TodoFilter.ALL}
            active={filter === TodoFilter.ALL}
          />
          <StatusButton
            text="Active"
            onClick={toggleFilter}
            value={TodoFilter.ACTIVE}
            active={filter === TodoFilter.ACTIVE}
          />
          <StatusButton
            text="Complete"
            onClick={toggleFilter}
            value={TodoFilter.COMPLETED}
            active={filter === TodoFilter.COMPLETED}
          />
        </Stack>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <ActionButton text="Clear Completed" onClick={clearCompleted} />
      </Box>
    </Stack>
  );

  const isNotMobileContent = (
    <Box>
      {isMobile ? isMobileStatusContent : isNotMobileStatusContent}
      <Box
        sx={(theme) => ({
          borderTop: '1px solid #e8e8e8',
        })}
      >
        {todos.length > 0 ? (
          todos.map((todo: Todo) => (
            <TodoItem todo={todo} handleUpdateTask={handleUpdateTask} />
          ))
        ) : (
          <Box
            sx={(theme) => ({
              width: '100%',
              paddingY: 3,
              paddingX: 3,
            })}
          >
            <Stack
              direction="row"
              spacing={4}
              className="w-full"
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="body1"
                sx={(theme) => ({
                  fontSize: '20px',
                  fontWeight: 'light',
                  textAlign: 'center',
                })}
              >
                {getEmptyLabel(filter)}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );

  return <>{isNotMobileContent}</>;
};

type TodoItemProps = {
  todo: Todo;
  handleUpdateTask: (id: string) => void;
};

const TodoItem: FC<TodoItemProps> = ({ todo, handleUpdateTask }) => {
  const { id, label, completed } = todo;

  return (
    <Box
      key={id}
      sx={(theme) => ({
        width: '100%',
        borderTop: '1px solid #f3f3f3',
        paddingY: 3,
        paddingX: 3,

        cursor: 'pointer', // Shows it's clickable
        userSelect: 'none', // Prevents text selection
        transition: 'all 0.2s ease', // Smooth hover/active

        // Hover effect
        '&:hover': {
          backgroundColor: '#fcfcfc',
        },

        // Active (clicked/pressed) effect
        '&:active': {
          backgroundColor: '#f8f8f8',
          color: '#4c4c4dff',
          transition: 'all 0.1s ease',
        },
      })}
      onClick={() => {
        handleUpdateTask(id);
      }}
    >
      <Stack
        direction="row"
        spacing={4}
        className="w-full"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Box>
          <Box
            sx={(theme) => ({
              width: '35px',
              height: '35px',
              backgroundColor: `${completed ? '#e1ffe3' : undefined}`,
              border: `${completed ? '1px solid #fff' : '1px solid #ddd'}`,
              borderRadius: '50%',
            })}
          ></Box>
        </Box>

        <Typography
          variant="body1"
          sx={(theme) => ({
            fontSize: '20px',
            fontWeight: 'light',
            textDecoration: completed ? 'line-through' : undefined,
            color: completed ? '#ccc' : '#656565',
          })}
        >
          {capitalize(label)}
        </Typography>
      </Stack>
    </Box>
  );
};

type ActionButtonProps = {
  text: string;
  onClick: () => void;
};

const ActionButton: FC<ActionButtonProps> = ({
  text,
  onClick,
  // value,
  // active,
}) => {
  return (
    <Box
      sx={{
        // backgroundColor: '#efefef' : undefined,
        borderRadius: '8px',
        padding: 1,
        paddingX: 2,
        cursor: 'pointer', // Shows it's clickable
        userSelect: 'none', // Prevents text selection
        transition: 'all 0.2s ease', // Smooth hover/active
      }}
      onClick={onClick}
    >
      <StyledText>{text}</StyledText>
    </Box>
  );
};

type StatusButtonProps = {
  text: string;
  onClick: (value: TodoFilter) => void;
  value: TodoFilter;
  active: boolean;
};

const StatusButton: FC<StatusButtonProps> = ({
  text,
  onClick,
  value,
  active,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: active ? '#efefef' : undefined,
        borderRadius: '8px',
        padding: 1,
        paddingX: 2,
        cursor: 'pointer', // Shows it's clickable
        userSelect: 'none', // Prevents text selection
        transition: 'all 0.2s ease', // Smooth hover/active
      }}
      onClick={() => {
        onClick(value);
      }}
    >
      <StyledText>{text}</StyledText>
    </Box>
  );
};

export default TodosList;
